import { useState, useEffect, useRef } from "react";
import { supabase } from './lib/supabase';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AuthScreen from "./components/AuthScreen";
import MonthCalendar from "./components/MonthCalendar";
import { Elements } from '@stripe/react-stripe-js';
import BatchesTab from './components/BatchesTab';
import InjectionSitesTab from './components/InjectionSitesTab';
import ReconstitutionTab from './components/ReconstitutionTab';
import { Compounds, LogEntries, BloodResults, Notes, Cycles, BodyEntries, Goal, CompoundLibrary, Batches, InjectionSites, CycleDoses } from './lib/data';
const stripePromise = new Promise((resolve) => {
  if (window.Stripe) {
    resolve(window.Stripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY));
  } else {
    const interval = setInterval(() => {
      if (window.Stripe) {
        clearInterval(interval);
        resolve(window.Stripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY));
      }
    }, 100);
  }
});
// ── Persistent storage helpers ──────────────────────────────────────────────
const KEYS = {
  compounds: "pt_compounds",
  logs:      "pt_logs",
  bloods:    "pt_bloods",
  notes:     "pt_notes",
  cycles:    "pt_cycles",
  body:      "pt_body",     // { entries: [], goal: { weight, heightCm } }
};

async function load(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

async function save(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const uid = () => Math.random().toString(36).slice(2, 9);
const today = () => new Date().toISOString().slice(0, 10);
const fmt = (d) => {
  if (!d) return "—";
  let dateStr = String(d).replace(' ', 'T').split('+')[0];
  if (!dateStr.includes('T')) {
    dateStr = dateStr + "T00:00:00";
  }
  const parsed = new Date(dateStr);
  if (isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" });
};
const DAY_MS = 86400000;

function expandScheduleDates(frequency, startDate, endDate) {
  const dates = [];
  const start = new Date(startDate + "T12:00:00Z");
  const end = endDate ? new Date(endDate + "T12:00:00Z") : new Date(start.getTime() + 90 * DAY_MS);

  const stepDaysMap = {
    "Daily": 1,
    "EOD": 2,
    "Weekly": 7,
    "Monthly": 30,
  };

  if (frequency === "2x/week") {
    let current = new Date(start);
    while (current <= end) {
      const day = current.getUTCDay();
      if (day === 1 || day === 4) {
        dates.push(current.toISOString().slice(0, 10));
      }
      current.setUTCDate(current.getUTCDate() + 1);
    }
    return dates;
  }

  const stepDays = stepDaysMap[frequency] || 7;
  let current = new Date(start);
  while (current <= end) {
    dates.push(current.toISOString().slice(0, 10));
    current.setUTCDate(current.getUTCDate() + stepDays);
  }
  return dates;
}



const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function getWeekDates(offsetWeeks = 0) {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - day + 1 + offsetWeeks * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toISOString().slice(0, 10);
  });
}

function cycleDayLabel(cycleStart, date) {
  if (!cycleStart) return null;
  const diff = Math.floor((new Date(date + "T00:00:00") - new Date(cycleStart + "T00:00:00")) / DAY_MS);
  if (diff < 0) return null;
  return `Day ${diff + 1}`;
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg: "#070B10",
  surface: "#0D1520",
  surface2: "#111D2E",
  border: "#1A2A40",
  accent: "#00D4FF",
  red: "#FF4D6A",
  green: "#00E5A0",
  yellow: "#FFD166",
  purple: "#A78BFA",
  orange: "#FB923C",
  pink: "#F472B6",
  text: "#E8F0F8",
  muted: "#5A7A99",
  mono: "'JetBrains Mono','Fira Mono',monospace",
  sans: "'Inter',system-ui,sans-serif",
};

const TABS = ["Dashboard", "Compounds", "Schedule", "Cycles | Protocols", "Dose Log", "Blood Work", "Notes", "Batches", "Reconstitution", "Injection Sites", "Export"];

// ── Half-life database (hours) ────────────────────────────────────────────────
const HALF_LIFE_DB = {
  // Testosterone esters
  "Testosterone Propionate":3,"Test Prop":3,"Test P":3,
  "Testosterone Enanthate":72,"Test E":72,"Test Enanthate":72,
  "Testosterone Cypionate":72,"Test C":72,"Test Cyp":72,
  "Testosterone Decanoate":264,"Test Deca":264,
  "Testosterone Undecanoate":120,"Test U":120,
  "Sustanon":96,
  // Nandrolone
  "Nandrolone Phenylpropionate":24,"NPP":24,
  "Nandrolone Decanoate":360,"Deca":360,"Deca-Durabolin":360,
  // Boldenone
  "Boldenone Undecylenate":120,"EQ":120,"Equipoise":120,
  // Trenbolone
  "Trenbolone Acetate":3,"Tren A":3,"Tren Ace":3,
  "Trenbolone Enanthate":72,"Tren E":72,
  "Trenbolone Hex":240,"Tren Hex":240,"Parabolan":240,
  // Masteron
  "Masteron Propionate":2.5,"Mast P":2.5,
  "Masteron Enanthate":72,"Mast E":72,
  // Orals
  "Dianabol":5,"Dbol":5,"Methandrostenolone":5,
  "Winstrol":9,"Winny":9,"Stanozolol":9,
  "Anavar":9,"Oxandrolone":9,
  "Turinabol":16,"Tbol":16,
  "Anadrol":16,"Oxymetholone":16,"Sdrol":8,
  "Superdrol":8,
  // Peptides & GH
  "BPC-157":1,"TB-500":0.5,"TB500":0.5,
  "Sermorelin":2,"GHRP-2":0.13,"GHRP-6":0.15,
  "Ipamorelin":2,"CJC-1295":504,"CJC-1295 w/o DAC":2,
  "IGF-1 LR3":20,"IGF-1":12,
  "HGH":2.5,"Growth Hormone":2.5,
  "Semaglutide":168,"Ozempic":168,"Wegovy":168,
  "Tirzepatide":146,"Zepbound":146,"Mounjaro":146,
  "Retatrutide":160,
  "MK-677":24,"Ibutamoren":24,
  // SARMs
  "Ostarine":24,"MK-2866":24,
  "Ligandrol":24,"LGD-4033":24,
  "Cardarine":16,"GW501516":16,
  "Andarine":3.6,"S4":3.6,
  "RAD-140":16,"Rad-140":16,
  "YK-11":6,"S23":12,
  // AI / PCT
  "Anastrozole":45,"Arimidex":45,
  "Letrozole":45,"Femara":45,
  "Exemestane":27,"Aromasin":27,
  "Clomiphene":120,"Clomid":120,
  "Tamoxifen":720,"Nolvadex":720,"Nolva":720,
  "Cabergoline":65,"Caber":65,
  // Other
  "Tadalafil":17.5,"Cialis":17.5,
  "Sildenafil":4,"Viagra":4,
  "Metformin":5,
};

function getHalfLife(name) {
  if (HALF_LIFE_DB[name]) return HALF_LIFE_DB[name];
  const lower = name.toLowerCase();
  for (const [k,v] of Object.entries(HALF_LIFE_DB)) {
    if (k.toLowerCase() === lower) return v;
  }
  return 48; // default
}

// Core PK calculation over an explicit time window (startMs to endMs)
function calcPKPointsInRange(doses, halfLifeHours, startMs, endMs) {
  if (!doses || doses.length === 0) return [];

  const points = [];

  // Use finer sampling for fast-acting compounds so short Tmax peaks aren't skipped
 const minTmax = Math.min(...doses.map(d => d.tmax_hours || 36));
let step;
if (minTmax <= 2) {
  step = 0.25 * 3600000;
} else if (minTmax <= 6) {
  step = 1 * 3600000;
} else {
  step = 4 * 3600000;
}


  for (let t = startMs; t <= endMs; t += step) {
    let level = 0;
    for (const d of doses) {
      if (!d.date) continue;

     let doseTime = String(d.date).trim().replace(' ', 'T');
const doseMs = new Date(doseTime).getTime();

      if (isNaN(doseMs)) {
        console.warn('Invalid dose date:', d.date);
        continue;
      }

      const hoursSince = (t - doseMs) / 3600000;

      if (hoursSince >= 0) {
        const tmax = d.tmax_hours || 36;
        const dose = parseFloat(d.dose) || 0;
        let doseLevel;

        if (hoursSince < tmax) {
          doseLevel = dose * (hoursSince / tmax);
        } else {
          const hoursSincePeak = hoursSince - tmax;
          const fastPhaseHours = d.fast_phase_hours || 96;
          const fastK = Math.LN2 / fastPhaseHours;
          const slowK = Math.LN2 / (d.half_life || halfLifeHours);
          const fastRatio = d.fast_ratio || 0.35;
          const slowRatio = 1 - fastRatio;

          const fastComponent = fastRatio * dose * Math.exp(-fastK * hoursSincePeak);
          const slowComponent = slowRatio * dose * Math.exp(-slowK * hoursSincePeak);

          doseLevel = fastComponent + slowComponent;
        }

        level += doseLevel;
      }
    }

    points.push({
      t,
      level: Math.max(0, level),
      label: new Date(t).toLocaleDateString("en-NZ", { day:"2-digit", month:"short" })
    });
  }

  return points;
}

// Dashboard's usual "past N days through now" view — unchanged behavior
function calcPKPoints(doses, halfLifeHours, rangeDays) {
  const now = new Date();
  const startMs = now.getTime() - rangeDays * DAY_MS;
  return calcPKPointsInRange(doses, halfLifeHours, startMs, now.getTime());
}

// For previewing a planned/future cycle — explicit forward-looking date range
function calcPKPointsForDateRange(doses, halfLifeHours, startDate, endDate) {
  const startMs = new Date(startDate + "T00:00:00").getTime();
  const endMs = new Date(endDate + "T23:59:59").getTime();
  return calcPKPointsInRange(doses, halfLifeHours, startMs, endMs);
}
const FREQ_OPTIONS = ["Daily", "EOD", "E3D", "Weekly", "As needed", "Custom"];
const ROUTES = ["SubQ", "IM", "Oral", "Transdermal", "Intranasal"];
const BLOOD_MARKERS = [
  "Testosterone (Total)","Testosterone (Free)","Estradiol (E2)","LH","FSH","SHBG",
  "Hematocrit","Hemoglobin","RBC","PSA","ALT","AST","GGT",
  "Lipids (LDL)","Lipids (HDL)","Triglycerides","IGF-1","HbA1c","Fasting Glucose","CRP","Custom",
];
const CHART_COLORS = [C.accent, C.green, C.yellow, C.red, C.purple, C.orange];

function catColor(c) {
  return ({ Peptide: C.accent, AAS: C.red, SARM: C.yellow, HRT: C.green, AI: C.purple, PCT: C.orange, Other: C.muted })[c] || C.muted;
}

// ── Reusable UI ───────────────────────────────────────────────────────────────

// ── Source Info / Confidence Badge ──────────────────────────────────────────
const CONFIDENCE_COLORS = {
  high: "#4ADE80",   // green
  medium: "#FBBF24", // amber
  low: "#F87171",    // red
  none: "#6B7280",   // grey
};

const CONFIDENCE_LABELS = {
  high: "High confidence",
  medium: "Medium confidence",
  low: "Low confidence",
  none: "No source data",
};

function SourceInfo({ confidence, citation, url, notes }) {
  const [open, setOpen] = useState(false);
  if (!confidence && !citation && !notes) return null;

  const color = CONFIDENCE_COLORS[confidence] || CONFIDENCE_COLORS.none;
  const label = CONFIDENCE_LABELS[confidence] || CONFIDENCE_LABELS.none;
  const isLowConfidence = confidence === 'low' || confidence === 'none';
  const symbol = isLowConfidence ? "!" : "i";

  return (
    <span style={{ position: "relative", display: "inline-block", marginLeft: 6 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: 16, height: 16,
          borderRadius: isLowConfidence ? 3 : "50%",
          background: isLowConfidence ? color + "22" : "transparent",
          border: `1.5px solid ${color}`,
          color, fontSize: 10, fontWeight: 700, lineHeight: "13px",
          cursor: "pointer", padding: 0, display: "inline-flex",
          alignItems: "center", justifyContent: "center",
        }}
        aria-label={isLowConfidence ? "Warning: low-confidence source data" : "Source information"}
      >{symbol}</button>

      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 40 }}
          />
          <div style={{
            position: "absolute", top: 22, left: 0, zIndex: 41,
            width: 260, background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 8, padding: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
              <span style={{ fontFamily: C.mono, fontSize: 10, color, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                {label}
              </span>
            </div>
            {citation && (
              <div style={{ fontSize: 12, color: C.text, marginBottom: 6, lineHeight: 1.4 }}>
                {citation}
              </div>
            )}
            {url && (
              <a href={url} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 11, color: C.accent, wordBreak: "break-all", display: "block", marginBottom: 6 }}>
                {url}
              </a>
            )}
            {notes && (
              <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4, borderTop: `1px solid ${C.border}`, paddingTop: 6, marginTop: 6 }}>
                {notes}
              </div>
            )}
          </div>
        </>
      )}
    </span>
  );
}

function Badge({ color, children }) {
  return (
    <span style={{ display:"inline-block", padding:"4px 10px", borderRadius:6, fontSize:12,
      fontFamily:C.mono, letterSpacing:"0.05em", fontWeight:600,
      background:color+"22", color, border:`1px solid ${color}44` }}>
      {children}
    </span>
  );
}

function Input({ label, ...props }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {label && <label style={{ fontSize:12, color:C.muted, fontFamily:C.mono, letterSpacing:"0.08em", textTransform:"uppercase" }}>{label}</label>}
      <input {...props} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
        color:C.text, fontFamily: props.type==="number" ? C.mono : C.sans,
        fontSize:15, padding:"10px 12px", outline:"none", width:"100%", boxSizing:"border-box", ...props.style }}
        onFocus={e => e.target.style.borderColor=C.accent}
        onBlur={e => e.target.style.borderColor=C.border} />
    </div>
  );
}

function Select({ label, children, ...props }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {label && <label style={{ fontSize:12, color:C.muted, fontFamily:C.mono, letterSpacing:"0.08em", textTransform:"uppercase" }}>{label}</label>}
      <select {...props} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
        color:C.text, fontFamily:C.sans, fontSize:15, padding:"10px 12px",
        outline:"none", width:"100%", boxSizing:"border-box", cursor:"pointer", ...props.style }}>
        {children}
      </select>
    </div>
  );
}

function Textarea({ label, ...props }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {label && <label style={{ fontSize:12, color:C.muted, fontFamily:C.mono, letterSpacing:"0.08em", textTransform:"uppercase" }}>{label}</label>}
      <textarea {...props} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
        color:C.text, fontFamily:C.sans, fontSize:15, padding:"10px 12px",
        outline:"none", width:"100%", boxSizing:"border-box", resize:"vertical", minHeight:90, ...props.style }}
        onFocus={e => e.target.style.borderColor=C.accent}
        onBlur={e => e.target.style.borderColor=C.border} />
    </div>
  );
}

function Btn({ variant="primary", children, ...props }) {
  const styles = {
    primary: { background:C.accent, color:C.bg, border:"none" },
    ghost:   { background:"transparent", color:C.muted, border:`1px solid ${C.border}` },
    danger:  { background:"transparent", color:C.red, border:`1px solid ${C.red}44` },
    green:   { background:C.green, color:C.bg, border:"none" },
  };
  return (
    <button {...props} style={{ ...styles[variant], borderRadius:8, padding:"10px 18px",
      fontSize:14, fontFamily:C.sans, fontWeight:600, cursor:"pointer", transition:"opacity 0.15s", ...props.style }}
      onMouseOver={e => e.currentTarget.style.opacity="0.8"}
      onMouseOut={e => e.currentTarget.style.opacity="1"}>
      {children}
    </button>
  );
}

function Card({ children, style }) {
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:20, ...style }}>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"#00000088",
      display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:20 }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14,
        padding:28, width:"100%", maxWidth:540, maxHeight:"90vh", overflowY:"auto" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22 }}>
          <span style={{ fontFamily:C.mono, fontSize:14, color:C.accent, letterSpacing:"0.08em" }}>{title}</span>
          <button onClick={onClose} style={{ background:"none", border:"none", color:C.muted, fontSize:22, cursor:"pointer" }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize:10, color:C.muted, fontFamily:C.mono, letterSpacing:"0.08em" }}>{label}</div>
      <div style={{ fontSize:13, color:C.text, fontFamily:C.mono }}>{value}</div>
    </div>
  );
}

// ── Tab: Dashboard ────────────────────────────────────────────────────────────
const RANGE_OPTIONS = [
  { label: "Week",    days: 7   },
  { label: "Month",   days: 30  },
  { label: "90 Days", days: 90  },
  { label: "All Time",days: 365 },
];

const GRAPH_COLORS = [C.accent, C.green, C.yellow, C.red, C.purple, C.orange, C.pink];

// ── Body entry modal ──────────────────────────────────────────────────────────
function BodyEntryModal({ onClose, onSave, goal, setGoal }) {
  const [form, setForm] = useState({ date: today(), weight: "", bodyFat: "", waist: "" });
  const [goalForm, setGoalForm] = useState({ weight: goal?.weight || "", heightCm: goal?.heightCm || "" });
  const [showGoal, setShowGoal] = useState(false);

  const save = () => {
    if (!form.weight && !form.bodyFat && !form.waist) return;
    onSave({ ...form, id: uid() });
    onClose();
  };

  const saveGoal = () => {
    setGoal({ weight: goalForm.weight, heightCm: goalForm.heightCm });
    setShowGoal(false);
  };

  return (
    <Modal title="LOG MEASUREMENTS" onClose={onClose}>
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <Input label="Date" type="date" value={form.date} onChange={e => setForm({...form, date:e.target.value})} />
<Input label="Time" type="time" value={form.time || '12:00'} onChange={e => setForm({...form, time:e.target.value})} />
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <Input label="Weight (kg)" type="number" value={form.weight} onChange={e => setForm({...form, weight:e.target.value})} placeholder="85.0" />
          <Input label="Body Fat (%)" type="number" value={form.bodyFat} onChange={e => setForm({...form, bodyFat:e.target.value})} placeholder="18.5" />
        </div>
        <Input label="Waist (cm)" type="number" value={form.waist} onChange={e => setForm({...form, waist:e.target.value})} placeholder="88.0" />

        <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
          <Btn variant="ghost" onClick={() => setShowGoal(s => !s)} style={{ fontSize:12 }}>⚙ Set Goal</Btn>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={save}>Save</Btn>
        </div>

        {showGoal && (
          <div style={{ background:C.bg, borderRadius:8, padding:14, border:`1px solid ${C.border}`, display:"flex", flexDirection:"column", gap:10 }}>
            <div style={{ fontFamily:C.mono, fontSize:11, color:C.accent, letterSpacing:"0.08em" }}>GOAL SETTINGS</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <Input label="Goal Weight (kg)" type="number" value={goalForm.weight} onChange={e => setGoalForm({...goalForm, weight:e.target.value})} placeholder="80.0" />
              <Input label="Height (cm)" type="number" value={goalForm.heightCm} onChange={e => setGoalForm({...goalForm, heightCm:e.target.value})} placeholder="178" />
            </div>
            <Btn onClick={saveGoal} style={{ alignSelf:"flex-end" }}>Save Goal</Btn>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Results section ───────────────────────────────────────────────────────────
function ResultsSection({ body, setBody }) {
  const [showModal, setShowModal] = useState(false);
  const [goal, setGoal] = useState(body?.goal || {});

  const entries = (body?.entries || []).slice().sort((a,b) => a.date.localeCompare(b.date));
  const latest  = entries[entries.length - 1];
  const first   = entries[0];

  const saveGoalAndPersist = (g) => {
    setGoal(g);
    const updated = { ...body, goal: g };
    setBody(updated);
    save(KEYS.body, updated);
  };

  const addEntry = (entry) => {
    const updated = { ...body, entries: [...(body?.entries || []), entry] };
    setBody(updated);
    save(KEYS.body, updated);
  };

  const delEntry = (id) => {
    const updated = { ...body, entries: (body?.entries || []).filter(e => e.id !== id) };
    setBody(updated);
    save(KEYS.body, updated);
  };

  // Stats
  const currentWeight  = latest  ? parseFloat(latest.weight)  : null;
  const startWeight    = first   ? parseFloat(first.weight)   : null;
  const goalWeight     = goal?.weight ? parseFloat(goal.weight) : null;
  const heightCm       = goal?.heightCm ? parseFloat(goal.heightCm) : null;

  const totalChange    = currentWeight != null && startWeight != null ? (currentWeight - startWeight) : null;
  const pctChange      = startWeight   ? (totalChange / startWeight * 100) : null;
  const toGoal         = currentWeight != null && goalWeight != null ? (currentWeight - goalWeight) : null;
  const toGoalPct      = goalWeight && startWeight ? Math.min(100, Math.max(0, Math.abs((startWeight - currentWeight) / (startWeight - goalWeight) * 100))) : null;
  const currentBMI     = currentWeight && heightCm ? (currentWeight / ((heightCm/100) ** 2)) : null;
  const currentBF      = latest?.bodyFat ? parseFloat(latest.bodyFat) : null;
  const currentWaist   = latest?.waist   ? parseFloat(latest.waist)   : null;

  // Weekly average change
  let weeklyAvg = null;
  if (entries.length >= 2) {
    const days = (new Date(latest.date) - new Date(first.date)) / DAY_MS;
    const weeks = days / 7;
    if (weeks > 0) weeklyAvg = totalChange / weeks;
  }

  // Weight chart data
  const weightData = entries.filter(e => e.weight).map(e => ({
    label: new Date(e.date + "T00:00:00").toLocaleDateString("en-NZ", { day:"2-digit", month:"short" }),
    weight: parseFloat(e.weight),
    bf: e.bodyFat ? parseFloat(e.bodyFat) : null,
  }));

  const statCard = (icon, label, value, sub, color = C.text) => (
    <Card style={{ flex:1, minWidth:130, padding:14 }}>
      <div style={{ fontSize:18, marginBottom:4 }}>{icon}</div>
      <div style={{ fontSize:10, color:C.muted, fontFamily:C.mono, letterSpacing:"0.08em", marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:20, fontWeight:700, color, fontFamily:C.mono }}>{value}</div>
      {sub && <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>{sub}</div>}
    </Card>
  );

  const changeColor = (v) => v == null ? C.text : v < 0 ? C.green : v > 0 ? C.red : C.text;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      {/* Section header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontFamily:C.mono, fontSize:11, color:C.muted, letterSpacing:"0.1em" }}>RESULTS</div>
          <div style={{ fontSize:18, fontWeight:700, color:C.text }}>Body Metrics</div>
        </div>
        <Btn onClick={() => setShowModal(true)} style={{ padding:"8px 14px", fontSize:12 }}>+ Log</Btn>
      </div>

      {entries.length === 0 ? (
        <Card style={{ textAlign:"center", padding:32 }}>
          <div style={{ fontSize:32, marginBottom:8 }}>⚖️</div>
          <div style={{ color:C.muted, fontSize:14 }}>No measurements yet.</div>
          <div style={{ color:C.muted, fontSize:12, marginTop:4 }}>Tap + Log to add your first entry.</div>
        </Card>
      ) : (
        <>
          {/* Stat grid */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
            {statCard("⚖️", "Weight", currentWeight != null ? `${currentWeight}kg` : "—", latest?.date ? fmt(latest.date) : null)}
            {statCard("📉", "Total Change", totalChange != null ? `${totalChange > 0 ? "+" : ""}${totalChange.toFixed(1)}kg` : "—", null, changeColor(totalChange))}
            {statCard("📊", "BMI", currentBMI ? currentBMI.toFixed(1) : heightCm ? "—" : "Set height", heightCm ? null : "in goal settings")}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
            {statCard("💪", "Body Fat", currentBF != null ? `${currentBF}%` : "—", null)}
            {statCard("📐", "Weekly Avg", weeklyAvg != null ? `${weeklyAvg > 0 ? "+" : ""}${weeklyAvg.toFixed(1)}kg/wk` : "—", null, changeColor(weeklyAvg))}
            {statCard("🎯", "To Goal", toGoal != null ? `${Math.abs(toGoal).toFixed(1)}kg` : goalWeight ? "—" : "Set goal", toGoalPct != null ? `${toGoalPct.toFixed(0)}% there` : null, C.accent)}
          </div>

          {/* Goal progress bar */}
          {toGoalPct != null && (
            <Card style={{ padding:14 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                <span style={{ fontFamily:C.mono, fontSize:11, color:C.muted }}>GOAL PROGRESS</span>
                <span style={{ fontFamily:C.mono, fontSize:11, color:C.accent }}>{toGoalPct.toFixed(0)}%</span>
              </div>
              <div style={{ background:C.border, borderRadius:4, height:8, overflow:"hidden" }}>
                <div style={{ width:`${toGoalPct}%`, height:"100%", background:`linear-gradient(90deg, ${C.green}, ${C.accent})`, borderRadius:4, transition:"width 0.4s" }} />
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", marginTop:6 }}>
                <span style={{ fontSize:10, color:C.muted }}>{startWeight}kg start</span>
                <span style={{ fontSize:10, color:C.accent }}>🎯 {goalWeight}kg goal</span>
              </div>
            </Card>
          )}

          {/* Weight chart */}
          {weightData.length >= 2 && (
            <Card>
              <div style={{ fontFamily:C.mono, fontSize:11, color:C.muted, letterSpacing:"0.1em", marginBottom:12 }}>WEIGHT OVER TIME</div>
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={weightData} margin={{ top:4, right:8, bottom:4, left:0 }}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill:C.muted, fontSize:9, fontFamily:C.mono }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:C.mono }} tickLine={false} axisLine={false} width={35}
                    domain={["auto","auto"]} tickFormatter={v => `${v}kg`} />
                  <Tooltip contentStyle={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, fontFamily:C.mono, fontSize:11 }}
                    labelStyle={{ color:C.muted }} itemStyle={{ color:C.green }}
                    formatter={(v) => [`${v}kg`, "Weight"]} />
                  {goalWeight && (
                    <Line type="monotone" dataKey={() => goalWeight} stroke={C.accent + "66"} strokeWidth={1}
                      strokeDasharray="4 4" dot={false} name="Goal" />
                  )}
                  <Line type="monotone" dataKey="weight" stroke={C.green} strokeWidth={2}
                    dot={{ fill:C.green, r:3 }} activeDot={{ r:5 }} name="Weight" />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Waist */}
          {currentWaist && (
            <Card style={{ padding:14 }}>
              <div style={{ fontFamily:C.mono, fontSize:11, color:C.muted, letterSpacing:"0.08em", marginBottom:4 }}>WAIST</div>
              <div style={{ fontSize:22, fontWeight:700, color:C.text, fontFamily:C.mono }}>{currentWaist}<span style={{ fontSize:13, color:C.muted }}> cm</span></div>
            </Card>
          )}

          {/* Recent entries */}
          <div>
            <div style={{ fontFamily:C.mono, fontSize:11, color:C.muted, letterSpacing:"0.1em", marginBottom:8 }}>RECENT ENTRIES</div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {[...entries].reverse().slice(0, 5).map(e => (
                <Card key={e.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px" }}>
                  <div>
                    <div style={{ fontFamily:C.mono, fontSize:11, color:C.accent }}>{fmt(e.date)}</div>
                    <div style={{ display:"flex", gap:16, marginTop:4, flexWrap:"wrap" }}>
                      {e.weight && <Stat label="Weight"  value={`${e.weight}kg`} />}
                      {e.bodyFat && <Stat label="BF%"   value={`${e.bodyFat}%`} />}
                      {e.waist   && <Stat label="Waist" value={`${e.waist}cm`} />}
                    </div>
                  </div>
                  <Btn variant="danger" style={{ padding:"4px 10px", fontSize:12 }} onClick={() => delEntry(e.id)}>✕</Btn>
                </Card>
              ))}
            </div>
          </div>
        </>
      )}

      {showModal && (
        <BodyEntryModal
          onClose={() => setShowModal(false)}
          onSave={addEntry}
          goal={goal}
          setGoal={saveGoalAndPersist}
        />
      )}
    </div>
  );
}

function DashboardTab({ compounds, logs, body, setBody }) {
  const [range, setRange] = useState(1); // index into RANGE_OPTIONS
  const activeCompounds = compounds.filter(c => c.active);
  const totalDoses = logs.length;
  const lastDose = logs.length > 0 ? logs[0] : null;
  // Group logs by compoundId
  const logsByCompound = logs.reduce((acc, l) => {
    if (!acc[l.compound_id]) acc[l.compound_id] = [];
    acc[l.compound_id].push(l);
    return acc;
}, {});
  const rangeDays = RANGE_OPTIONS[range].days;

  // Split active compounds into full-PK vs simple-log tiers
  const pkCompounds = activeCompounds.filter(c => c.tracking_type !== 'simple_log');
  const simpleLogCompounds = activeCompounds.filter(c => c.tracking_type === 'simple_log');

  // Build PK data per compound — only for full PK-curve compounds
const compoundGraphs = pkCompounds.map((c, i) => {
  const doses = logsByCompound[c.id] || [];
  const halfLife = c.half_life || getHalfLife(c.name);
  const points = calcPKPoints(doses, halfLife, rangeDays);
  if (c.name === 'Anavar (Oxandrolone)') {
    const maxPoint = points.reduce((max, p) => p.level > max.level ? p : max, points[0]);
    console.log('Max point raw t:', maxPoint.t, 'as UTC ISO:', new Date(maxPoint.t).toISOString(), 'as local:', new Date(maxPoint.t).toLocaleString());
  }
  const currentLevel = points.length > 0 ? points[points.length - 1].level : 0;
  const peak = points.length > 0 ? Math.max(...points.map(p => p.level)) : 0;
  return { compound: c, doses, halfLife, points, currentLevel, peak, color: GRAPH_COLORS[i % GRAPH_COLORS.length] };
});
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Summary stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
        {[
          { icon: "💉", label: "Doses Logged", value: totalDoses },
          { icon: "⚗️", label: "Active", value: activeCompounds.length },
        ].map(s => (
          <Card key={s.label} style={{ textAlign: "center", padding: 14 }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontSize: 10, color: C.muted, fontFamily: C.mono, letterSpacing: "0.08em", marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.text, fontFamily: C.mono }}>{s.value}</div>
    
          </Card>
        ))}
      </div>

      {/* Range selector */}
      <div style={{ display: "flex", gap: 6 }}>
        {RANGE_OPTIONS.map((r, i) => (
          <button key={r.label} onClick={() => setRange(i)} style={{
            flex: 1, background: range === i ? C.accent + "22" : "transparent",
            border: `1px solid ${range === i ? C.accent : C.border}`,
            color: range === i ? C.accent : C.muted,
            borderRadius: 8, padding: "8px 0", fontSize: 12,
            fontFamily: C.mono, cursor: "pointer", fontWeight: 600,
          }}>{r.label}</button>
        ))}
      </div>

      {activeCompounds.length === 0 && (
  <Card style={{ textAlign: "center", padding: 40 }}>
    <div style={{ color: C.muted, fontSize: 14 }}>Add compounds and log doses to see level graphs.</div>
  </Card>
)}

{simpleLogCompounds.length > 0 && (
  <Card>
    <div style={{ fontFamily: C.mono, fontSize: 11, color: C.muted, letterSpacing: "0.08em", marginBottom: 12 }}>
      SIMPLE TRACKING (no PK curve)
    </div>
    {Object.entries(
      simpleLogCompounds.reduce((groups, c) => {
        const cat = c.category || 'Other';
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(c);
        return groups;
      }, {})
    )
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, compoundsInGroup]) => (
        <div key={category} style={{ marginBottom: 14 }}>
          <div style={{ fontFamily: C.mono, fontSize: 10, color: C.accent, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>
            {category}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {compoundsInGroup
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(c => {
                const doses = (logsByCompound[c.id] || []).slice().sort((a, b) => new Date(b.date) - new Date(a.date));
                const lastDose = doses[0];
                return (
                  <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, display: "flex", alignItems: "center" }}>
                        {c.name}
                        <SourceInfo confidence={c.confidence} citation={c.source_citation} url={c.source_url} notes={c.source_notes} />
                      </div>
                      <div style={{ fontSize: 11, color: C.muted }}>
                        {lastDose ? `Last dose: ${lastDose.dose ?? "—"}${lastDose.unit ? " " + lastDose.unit : ""} · ${fmt(lastDose.date)}` : "No doses logged"}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, fontFamily: C.mono }}>
                      {doses.length} dose{doses.length !== 1 ? "s" : ""}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      ))}
  </Card>
)}

    {/* Per-compound graph cards */}
{compoundGraphs.map(g => {
  const maxLevel = Math.max(...g.points.map(p => p.level), 1);
// Downsample for chart — show every Nth point, but keep more detail for fast-acting compounds
// so short absorption peaks (e.g. orals with 1-2h Tmax) aren't smoothed away
const targetPoints = g.compound.tmax_hours && g.compound.tmax_hours <= 6 ? 300 : 80;
const step = Math.max(1, Math.floor(g.points.length / targetPoints));
const chartData = g.points.filter((_, i) => i % step === 0).map(p => ({
  ...p,
  historicalLevel: p.level,
  projectedLevel: null,
}));
// Add 45-day projection using the SAME bi-exponential model as calcPKPoints
const lastPoint = g.points[g.points.length - 1];
if (lastPoint) {
  const startMs = lastPoint.t;

  // Bridge point - connects solid to dashed
  chartData.push({
    t: lastPoint.t,
    label: lastPoint.label,
    historicalLevel: lastPoint.level,
    projectedLevel: lastPoint.level,
  });

  const stepMs = 4 * 3600000; // same 4-hour sampling as calcPKPoints
const endMs = startMs + rangeDays * DAY_MS;

  for (let t = startMs + stepMs; t <= endMs; t += stepMs) {
    let level = 0;

    for (const d of g.doses) {
      if (!d.date) continue;
    let doseTime = String(d.date).trim().replace(' ', 'T');
const doseMs = new Date(doseTime).getTime();
      if (isNaN(doseMs)) continue;

      const hoursSince = (t - doseMs) / 3600000;
      if (hoursSince < 0) continue;

      const tmax = d.tmax_hours || 36;
      const dose = parseFloat(d.dose) || 0;
      let doseLevel;

      if (hoursSince < tmax) {
        doseLevel = dose * (hoursSince / tmax);
      } else {
        const hoursSincePeak = hoursSince - tmax;
        const fastPhaseHours = d.fast_phase_hours || 96;
        const fastK = Math.LN2 / fastPhaseHours;
        const slowK = Math.LN2 / (d.half_life || g.halfLife);
        const fastRatio = d.fast_ratio || 0.35;
        const slowRatio = 1 - fastRatio;

        const fastComponent = fastRatio * dose * Math.exp(-fastK * hoursSincePeak);
        const slowComponent = slowRatio * dose * Math.exp(-slowK * hoursSincePeak);

        doseLevel = fastComponent + slowComponent;
      }

      level += doseLevel;
    }

    chartData.push({
      t,
      label: new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      historicalLevel: null,
      projectedLevel: Math.max(0, level),
    });
  }
}

        return (
          <Card key={g.compound.id}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
              <div>
               <div style={{ fontWeight: 700, fontSize: 15, color: C.text, display: "flex", alignItems: "center" }}>
  {g.compound.name}
  <SourceInfo confidence={g.compound.confidence} citation={g.compound.source_citation} url={g.compound.source_url} notes={g.compound.source_notes} />
</div>
                <div style={{ fontSize: 11, color: C.muted, fontFamily: C.mono, marginTop: 2 }}>
                  Half-life: {g.halfLife}h · {g.doses.length} dose{g.doses.length !== 1 ? "s" : ""} logged
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: g.color, fontFamily: C.mono }}>
                  {g.currentLevel.toFixed(2)}
                </div>
                <div style={{ fontSize: 10, color: C.muted }}>est. current</div>
              </div>
            </div>

          {/* Graph */}
{g.points.length < 2 ? (
  <div style={{ textAlign: "center", padding: "40px 0", color: C.muted, fontSize: 12 }}>
    Log at least one dose to see the level graph
  </div>
) : (
  <ResponsiveContainer width="100%" height={320}>
    <LineChart data={chartData} margin={{ top: 16, right: 16, bottom: 16, left: -20 }}>
      <defs>
        <linearGradient id={`grad-${g.compound.id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={g.color} stopOpacity={0.4} />
          <stop offset="60%" stopColor={g.color} stopOpacity={0.15} />
          <stop offset="100%" stopColor={g.color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <CartesianGrid stroke={C.border} strokeDasharray="2 4" vertical={false} opacity={0.5} />
      <XAxis 
        dataKey="label" 
        tick={{ fill: C.muted, fontSize: 10, fontFamily: C.mono }} 
        interval="preserveStartEnd" 
        tickLine={false} 
        axisLine={false}
      />
      <YAxis 
        tick={{ fill: C.muted, fontSize: 10, fontFamily: C.mono }} 
        tickLine={false} 
        axisLine={false} 
        width={40}
        tickFormatter={v => v.toFixed(1)}
      />
     <Tooltip
  contentStyle={{ background: C.surface, border: `2px solid ${g.color}`, borderRadius: 8, fontFamily: C.mono, fontSize: 12, boxShadow: `0 4px 12px rgba(0,212,255,0.15)` }}
  labelStyle={{ color: C.accent }}
  formatter={(v) => [parseFloat(v).toFixed(3), "Level"]}
  labelFormatter={(label, payload) => {
    if (payload && payload.length > 0 && payload[0].payload.t) {
      const d = new Date(payload[0].payload.t);
      return d.toLocaleString("en-NZ", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
    }
    return label;
  }}
  itemStyle={{ color: g.color, fontWeight: 600 }}
  cursor={{ strokeDasharray: '5 5', stroke: g.color }}
/>
<Line 
  type="monotone" 
  strokeLinecap="round"
  strokeLinejoin="round"
  dataKey="historicalLevel" 
  stroke={g.color} 
  strokeWidth={3}
  fill={`url(#grad-${g.compound.id})`}
  dot={false} 
  activeDot={{ r: 6, fill: g.color, strokeWidth: 2 }}
  isAnimationActive={true}
  connectNulls={false}
/>

{/* 45-day projection */}
<Line 
  type="monotone" 
  dataKey="projectedLevel" 
  stroke={g.color} 
  strokeWidth={2}
  strokeDasharray="5 5"
  dot={false}
  isAnimationActive={false}
  connectNulls={true}
/>

    </LineChart>
  </ResponsiveContainer>
)}
            {/* Stats row */}
            <div style={{ display: "flex", gap: 20, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
              <Stat label="Current" value={g.currentLevel.toFixed(2)} />
              <Stat label="Peak" value={g.peak.toFixed(2)} />
              <Stat label="Half-life" value={`${g.halfLife}h`} />
              {g.doses.length > 0 && (
                <Stat label="Last Dose" value={`${g.doses[0]?.dose}${g.doses[0]?.unit} · ${fmt(g.doses[0]?.date)}`} />
              )}
            </div>
          </Card>
        );
      })}

      {/* ── Results section ── */}
      <div style={{ borderTop:`1px solid ${C.border}`, margin:"8px 0" }} />
      <ResultsSection body={body} setBody={setBody} />
    </div>
  );
}

// ── Tab: Compounds ────────────────────────────────────────────────────────────
function CompoundsTab({ compounds, setCompounds }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name:"", category:"Peptide", dose:"", unit:"mcg", half_life:"", tmax_hours:"", fast_ratio:"", frequency:"Daily", route:"SubQ", active:true, notes:"" });
  const [library, setLibrary] = useState([]);

  useEffect(() => {
    CompoundLibrary.list().then(setLibrary).catch(err => console.error('Failed to load library:', err));
  }, []);

  const cats = ["Peptide","AAS","SARM","HRT","AI","PCT","Other"];

 const add = async () => {
  if (!form.name.trim()) return;
  const newCompound = { 
    ...form,
    created_at: new Date().toISOString(),
    dose: form.dose ? parseFloat(form.dose) : null,
    half_life: form.half_life ? parseFloat(form.half_life) : null,
    tmax_hours: form.tmax_hours ? parseFloat(form.tmax_hours) : 12,
    fast_ratio: form.fast_ratio ? parseFloat(form.fast_ratio) : 0.35,
    tracking_type: form.tracking_type || 'pk_curve',
  };
  delete newCompound.id;
    try {
      const added = await Compounds.add(newCompound);
      setCompounds([...compounds, added]);
      setShowForm(false);
     setForm({ name:"", category:"Peptide", dose:"", unit:"mcg", half_life:"", tmax_hours:"", fast_ratio:"", frequency:"Daily", route:"SubQ", active:true, notes:"" });
    } catch (err) {
      console.error('Failed to add compound:', err);
    }
  };

  const toggle = async (id) => {
    const c = compounds.find(x => x.id === id);
    if (!c) return;
    try {
      await Compounds.update(id, { active: !c.active });
      setCompounds(compounds.map(x => x.id === id ? {...x, active: !x.active} : x));
    } catch (err) {
      console.error('Failed to toggle:', err);
    }
  };

  const del = async (id) => {
    try {
      await Compounds.remove(id);
      setCompounds(compounds.filter(c => c.id !== id));
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const active = compounds.filter(c => c.active);
  const inactive = compounds.filter(c => !c.active);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontFamily:C.mono, fontSize:11, color:C.muted, letterSpacing:"0.1em" }}>PROTOCOL</div>
          <div style={{ fontSize:20, fontWeight:700, color:C.text }}>{active.length} active compound{active.length!==1?"s":""}</div>
        </div>
        <Btn onClick={() => setShowForm(true)}>+ Add</Btn>
      </div>

      {active.map(c => (
        <Card key={c.id}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:600, fontSize:15, color:C.text }}>{c.name}</div>
              <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>{c.category} · {c.dose} {c.unit} {c.frequency} · {c.route}</div>
            </div>
            <div style={{ display:"flex", gap:6 }}>
              <Btn variant="ghost" style={{ padding:"4px 10px", fontSize:12 }} onClick={() => toggle(c.id)}>Pause</Btn>
              <Btn variant="danger" style={{ padding:"4px 10px", fontSize:12 }} onClick={() => del(c.id)}>✕</Btn>
            </div>
          </div>
        </Card>
      ))}

      {inactive.length > 0 && (
        <>
          <div style={{ marginTop:16, fontSize:12, color:C.muted, fontFamily:C.mono }}>PAUSED</div>
          {inactive.map(c => (
            <Card key={c.id} style={{ opacity:0.6 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:15, color:C.text }}>{c.name}</div>
                  <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>{c.category} · {c.dose} {c.unit}</div>
                </div>
                <div style={{ display:"flex", gap:6 }}>
                  <Btn variant="ghost" style={{ padding:"4px 10px", fontSize:12 }} onClick={() => toggle(c.id)}>Activate</Btn>
                  <Btn variant="danger" style={{ padding:"4px 10px", fontSize:12 }} onClick={() => del(c.id)}>✕</Btn>
                </div>
              </div>
            </Card>
          ))}
        </>
      )}
{showForm && (
  <Modal title="NEW COMPOUND" onClose={() => setShowForm(false)}>
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <Select label="Library" onChange={e => { const lib = library.find(l => l.name === e.target.value); if (lib) setForm({...form, name:lib.name, category:lib.category, half_life:lib.half_life_hours || "", tmax_hours:lib.tmax_hours || 12, fast_ratio:lib.fast_ratio || 0.35, fast_phase_hours:lib.fast_phase_hours || 96, tracking_type:lib.tracking_type || 'pk_curve', confidence:lib.confidence || '', source_citation:lib.source_citation || '', source_url:lib.source_url || '', source_notes:lib.source_notes || '', route:lib.route || 'SubQ'}); }}>
        <option value="">Select from library...</option>
        {Object.entries(
          library.reduce((groups, l) => {
            const cat = l.category || 'Other';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(l);
            return groups;
          }, {})
        )
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([category, compounds]) => (
            <optgroup key={category} label={category}>
              {compounds
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(l => <option key={l.name} value={l.name}>{l.name}</option>)}
            </optgroup>
          ))}
      </Select>
      {form.confidence && (
        <div style={{ display: "flex", alignItems: "center", fontSize: 12, color: C.muted, fontFamily: C.mono }}>
          Sourcing for this compound's PK data
          <SourceInfo confidence={form.confidence} citation={form.source_citation} url={form.source_url} notes={form.source_notes} />
        </div>
      )}

      <Input label="Name" value={form.name} onChange={e => setForm({...form, name:e.target.value})} placeholder="e.g. Testosterone Enanthate" />
      <Select label="Category" value={form.category} onChange={e => setForm({...form, category:e.target.value})}>
        {cats.map(c => <option key={c}>{c}</option>)}
      </Select>
      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:10 }}>
        <Input label="Dose" type="number" value={form.dose} onChange={e => setForm({...form, dose:e.target.value})} placeholder="500" />
        <Select label="Unit" value={form.unit} onChange={e => setForm({...form, unit:e.target.value})}>
          {["mg","mcg","IU","ml"].map(u => <option key={u}>{u}</option>)}
        </Select>
      </div>
      {form.tracking_type !== 'simple_log' && (
        <>
          <Input label="Half-life (hours)" type="number" value={form.half_life} onChange={e => setForm({...form, half_life:e.target.value})} placeholder="0" />
          <Input label="Time to Peak (Tmax, hours)" type="number" value={form.tmax_hours || ''} onChange={e => setForm({...form, tmax_hours:e.target.value})} placeholder="12" step="0.1" />
          <Input label="Fast Distribution Ratio (0-1)" type="number" value={form.fast_ratio || 0.35} onChange={e => setForm({...form, fast_ratio: parseFloat(e.target.value)})} placeholder="0.35" step="0.05" min="0" max="1" />
        </>
      )}
      <Select label="Frequency" value={form.frequency} onChange={e => setForm({...form, frequency:e.target.value})}>
        {["Daily","EOD","2x/week","Weekly","Monthly"].map(f => <option key={f}>{f}</option>)}
      </Select>
      <Select label="Route" value={form.route} onChange={e => setForm({...form, route:e.target.value})}>
        {["SubQ","IM","IV","Oral","Topical"].map(r => <option key={r}>{r}</option>)}
      </Select>
       <Input label="Notes" value={form.notes} onChange={e => setForm({...form, notes:e.target.value})} placeholder="Optional" />
      <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
        <Btn variant="ghost" onClick={() => setShowForm(false)}>Cancel</Btn>
        <Btn onClick={add}>Save</Btn>
      </div>
    </div>
  </Modal>
)}
    </div>
  );
}
// ── Tab: Schedule ─────────────────────────────────────────────────────────────
function ScheduleTab({ compounds, logs, cycles }) {
  const [weekOffset, setWeekOffset] = useState(0);
  const weekDates = getWeekDates(weekOffset);
  const activeCycle = cycles.find(c => !c.endDate || c.endDate >= today());

  // For each day, find logged compounds
  const logsOnDay = (date) => logs.filter(l => l.date === date);

  const isToday = (date) => date === today();

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {/* Cycle banner */}
      {activeCycle && (
        <Card style={{ background:C.surface2, borderColor:C.accent+"44", padding:"12px 16px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <div style={{ fontFamily:C.mono, fontSize:11, color:C.accent, letterSpacing:"0.1em" }}>ACTIVE CYCLE</div>
              <div style={{ fontSize:15, fontWeight:700, color:C.text, marginTop:2 }}>{activeCycle.name}</div>
            </div>
            <div style={{ textAlign:"right" }}>
              <div style={{ fontFamily:C.mono, fontSize:22, fontWeight:700, color:C.accent }}>
                {cycleDayLabel(activeCycle.startDate, today())}
              </div>
              <div style={{ fontFamily:C.mono, fontSize:10, color:C.muted }}>
                {fmt(activeCycle.startDate)}{activeCycle.endDate ? ` → ${fmt(activeCycle.endDate)}` : " → ongoing"}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Week nav */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <Btn variant="ghost" style={{ padding:"6px 12px" }} onClick={() => setWeekOffset(w => w-1)}>← Prev</Btn>
        <span style={{ fontFamily:C.mono, fontSize:11, color:C.muted, letterSpacing:"0.08em" }}>
          {fmt(weekDates[0])} – {fmt(weekDates[6])}
        </span>
        <Btn variant="ghost" style={{ padding:"6px 12px" }} onClick={() => setWeekOffset(w => w+1)}>Next →</Btn>
      </div>

      {/* Week grid */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:6 }}>
        {weekDates.map((date, i) => {
          const dayLogs = logsOnDay(date);
          const cdLabel = activeCycle ? cycleDayLabel(activeCycle.startDate, date) : null;
          return (
            <div key={date} style={{
              background: isToday(date) ? C.accent+"18" : C.surface,
              border: `1px solid ${isToday(date) ? C.accent+"66" : C.border}`,
              borderRadius:8, padding:"8px 6px", minHeight:90,
            }}>
              <div style={{ fontFamily:C.mono, fontSize:10, color: isToday(date) ? C.accent : C.muted, marginBottom:2 }}>
                {DAYS_OF_WEEK[(i+1)%7]}
              </div>
              <div style={{ fontFamily:C.mono, fontSize:11, color: isToday(date) ? C.accent : C.text, fontWeight:700, marginBottom:4 }}>
                {date.slice(8)}
              </div>
              {cdLabel && (
                <div style={{ fontFamily:C.mono, fontSize:9, color:C.accent+"99", marginBottom:4 }}>{cdLabel}</div>
              )}
              <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                {dayLogs.map(l => (
                  <div key={l.id} style={{
                    fontSize:9, color:C.text, background:C.border+"99",
                    borderRadius:3, padding:"2px 4px", overflow:"hidden",
                    textOverflow:"ellipsis", whiteSpace:"nowrap",
                  }}>{l.compoundName}</div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Active compounds this week */}
      {compounds.filter(c=>c.active).length > 0 && (
        <Card>
          <div style={{ fontFamily:C.mono, fontSize:11, color:C.muted, letterSpacing:"0.1em", marginBottom:12 }}>ACTIVE PROTOCOL</div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {compounds.filter(c=>c.active).map(c => (
              <div key={c.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ width:6, height:6, borderRadius:"50%", background:catColor(c.category) }} />
                  <span style={{ fontSize:13, color:C.text }}>{c.name}</span>
                </div>
                <div style={{ display:"flex", gap:12 }}>
                  <span style={{ fontFamily:C.mono, fontSize:11, color:C.muted }}>{c.dose}{c.unit}</span>
                  <span style={{ fontFamily:C.mono, fontSize:11, color:C.muted }}>{c.frequency}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ── Tab: Log Dose ──────────────────────────────────────────────────────────────────
function LogTab({ compounds, logs, setLogs, cycles }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date:today(), time:"12:00", compoundId:"", dose:"", unit:"mcg", site:"", notes:"" });
  const [quickAddText, setQuickAddText] = useState("");
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [quickAddError, setQuickAddError] = useState("");
  const [confirmingId, setConfirmingId] = useState(null);
  const [confirmDose, setConfirmDose] = useState("");
  const [confirmTime, setConfirmTime] = useState("12:00");
  const SITES = ["Left delt","Right delt","Left glute","Right glute","Left quad","Right quad","Abdomen L","Abdomen R","Other"];
  const activeCycle = cycles.find(c => !c.endDate || c.endDate >= today());

  const handleQuickAdd = async () => {
    if (!quickAddText.trim()) return;
    setQuickAddLoading(true);
    setQuickAddError("");
    try {
      const compoundNames = compounds.map(c => c.name);
      const response = await fetch('/.netlify/functions/parse-dose', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: quickAddText, compoundNames }),
      });
      if (!response.ok) throw new Error('Failed to parse');
      const parsed = await response.json();

      const matchedCompound = compounds.find(c => c.name === parsed.compound)
        || compounds.find(c => c.name.toLowerCase().includes((parsed.compound || '').toLowerCase()))
        || compounds.find(c => (parsed.compound || '').toLowerCase().includes(c.name.toLowerCase()));

      setForm({
        date: parsed.date || today(),
        time: parsed.time || "12:00",
        compoundId: matchedCompound?.id || "",
        dose: parsed.dose || "",
        unit: parsed.unit || matchedCompound?.unit || "mcg",
        site: "",
        notes: "",
      });
           setQuickAddText("");
      setShowForm(true);
    } catch (err) {
      console.error('Quick add parse failed:', err);
      setQuickAddError("Couldn't parse that — try the manual form instead.");
    } finally {
      setQuickAddLoading(false);
    }
  };

  const confirmPlannedDose = async (log) => {
    try {
      const originalDate = String(log.date).slice(0, 10);
      await LogEntries.update(log.id, {
        dose: parseFloat(confirmDose) || log.dose,
        date: new Date(`${originalDate}T${confirmTime}:00`).toISOString(),
        status: 'completed',
      });
      setLogs(prev => prev.map(l => l.id === log.id ? { ...l, dose: parseFloat(confirmDose) || l.dose, status: 'completed' } : l));
      setConfirmingId(null);
    } catch (err) {
      console.error('Failed to confirm dose:', err);
    }
  };

    useEffect(() => {
    if (confirmingId) {
      const log = logs.find(l => l.id === confirmingId);
      if (log) {
        setConfirmDose(String(log.dose || ""));
        const timeMatch = String(log.date).match(/T(\d{2}:\d{2})/);
        setConfirmTime(timeMatch ? timeMatch[1] : "12:00");
      }
    }
   }, [confirmingId]);

const add = async () => {
  if (!form.compoundId || !form.date) return;
  const compound = compounds.find(c => c.id===form.compoundId);
  const localDateTime = new Date(`${form.date}T${form.time}:00`);
  const entry = { 
    ...form, 
    date: localDateTime.toISOString(),
    compound_id: form.compoundId,
    compound_name: compound?.name||"Unknown",
    dose: form.dose ? parseFloat(form.dose) : compound?.dose||null,
    unit: form.unit||compound?.unit||"mcg",
    tmax_hours: compound?.tmax_hours || 12,
    half_life: compound?.half_life || 0,
    fast_ratio: compound?.fast_ratio,
    fast_phase_hours: compound?.fast_phase_hours,
    created_at: new Date().toISOString(),
  };
  delete entry.compoundId;
  delete entry.time; 
  try {
    const added = await LogEntries.add(entry);
    setLogs([added, ...logs]);
    setShowForm(false);
    setForm({ date:today(), time:"12:00", compoundId:"", dose:"", unit:"mcg", site:"", notes:"" });
  } catch (err) {
    console.error('Failed to add log:', err);
  }
};

const del = async (id) => {
    try {
      await LogEntries.remove(id);
      setLogs(logs.filter(l => l.id!==id));
    } catch (err) {
      console.error('Failed to delete log:', err);
    }
  };


  const grouped = logs.reduce((acc, l) => {
    if (!acc[l.date]) acc[l.date] = [];
    acc[l.date].push(l); return acc;
  }, {});

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontFamily:C.mono, fontSize:11, color:C.muted, letterSpacing:"0.1em" }}>ADMINISTRATION LOG</div>
          <div style={{ fontSize:20, fontWeight:700, color:C.text }}>{logs.length} entries</div>
        </div>
        <Btn onClick={() => setShowForm(true)}>+ Log Dose</Btn>
      </div>

      <div style={{ display:"flex", gap:8 }}>
        <Input
          value={quickAddText}
          onChange={e => setQuickAddText(e.target.value)}
          placeholder="e.g. took 200mg deca yesterday"
          onKeyDown={e => e.key === 'Enter' && handleQuickAdd()}
          style={{ flex:1 }}
        />
        <Btn onClick={handleQuickAdd} disabled={quickAddLoading}>
          {quickAddLoading ? "..." : "Quick Add"}
        </Btn>
      </div>
      {quickAddError && <div style={{ color:C.red, fontSize:13 }}>{quickAddError}</div>}
      {logs.length===0 && <Card style={{ textAlign:"center", padding:40 }}><div style={{ color:C.muted, fontSize:14 }}>No doses logged yet.</div></Card>}

      {Object.keys(grouped).sort((a,b) => b.localeCompare(a)).map(date => (
  <div key={date}>
    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
      <div style={{ fontFamily:C.mono, fontSize:11, color:C.accent, letterSpacing:"0.1em" }}>{fmt(date)}</div>
      {activeCycle && cycleDayLabel(activeCycle.startDate, date) && (
        <Badge color={C.accent}>{cycleDayLabel(activeCycle.startDate, date)}</Badge>
      )}
    </div>
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {grouped[date].map(l => {
        const isPlanned = l.status === 'planned';
        const isConfirming = confirmingId === l.id;
        return (
          <Card key={l.id} style={{ padding:"12px 16px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ fontWeight:600, fontSize:14, color:C.text }}>{l.compoundName || l.compound_name}</div>
                  {isPlanned && (
                    <span style={{
                      fontSize:9, color:C.accent, fontFamily:C.mono, letterSpacing:"0.06em",
                      padding:"2px 7px", borderRadius:5, border:`1px solid ${C.accent}44`,
                    }}>PLANNED</span>
                  )}
                </div>
                <div style={{ display:"flex", gap:12, marginTop:4 }}>
                  {l.dose && <Stat label="Dose" value={`${l.dose} ${l.unit}`} />}
                  {l.site && <Stat label="Site" value={l.site} />}
                </div>
                {l.notes && <div style={{ fontSize:12, color:C.muted, marginTop:6 }}>{l.notes}</div>}
              </div>
              <div style={{ display:"flex", gap:6 }}>
                {isPlanned && (
                  <Btn variant="ghost" style={{ padding:"4px 10px", fontSize:12 }} onClick={() => setConfirmingId(isConfirming ? null : l.id)}>
                    Log
                  </Btn>
                )}
                <Btn variant="danger" style={{ padding:"4px 10px", fontSize:12 }} onClick={() => del(l.id)}>✕</Btn>
              </div>
            </div>

            {isConfirming && (
              <div style={{ marginTop:10, paddingTop:10, borderTop:`1px solid ${C.border}` }}>
                <div style={{ display:"flex", gap:8 }}>
                  <Input type="number" value={confirmDose} onChange={e => setConfirmDose(e.target.value)} placeholder="Dose" style={{ flex:1 }} />
                  <Input type="time" value={confirmTime} onChange={e => setConfirmTime(e.target.value)} style={{ flex:1 }} />
                </div>
                <div style={{ display:"flex", gap:8, marginTop:8 }}>
                  <Btn variant="ghost" onClick={() => setConfirmingId(null)} style={{ flex:1 }}>Cancel</Btn>
                  <Btn onClick={() => confirmPlannedDose(l)} style={{ flex:2 }}>Confirm Dose</Btn>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  </div>
))}
      {showForm && (
        <Modal title="LOG DOSE" onClose={() => setShowForm(false)}>
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <Input label="Date" type="date" value={form.date} onChange={(e) => setForm({...form, date:e.target.value})} />
              <Input label="Time" type="time" value={form.time} onChange={e => setForm({...form, time:e.target.value})} />
            <Select label="Compound" value={form.compoundId} onChange={(e) => setForm({...form, compoundId:e.target.value})}>
              <option value="">— Select compound —</option>
              {compounds.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:10 }}>
              <Input label="Dose" type="number" value={form.dose} onChange={e => setForm({...form, dose:e.target.value})} />
              <Select label="Unit" value={form.unit} onChange={e => setForm({...form, unit:e.target.value})}>
                {["mcg","mg","IU","ml"].map(u => <option key={u}>{u}</option>)}
              </Select>
            </div>
            <Select label="Injection Site" value={form.site} onChange={e => setForm({...form, site:e.target.value})}>
              <option value="">— Optional —</option>
              {SITES.map(s => <option key={s}>{s}</option>)}
            </Select>
            <Textarea label="Notes" value={form.notes} onChange={e => setForm({...form, notes:e.target.value})} placeholder="Observations, timing..." />
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <Btn variant="ghost" onClick={() => setShowForm(false)}>Cancel</Btn>
              <Btn onClick={add}>Log Dose</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Tab: Blood Work ───────────────────────────────────────────────────────────
function BloodTab({ bloods, setBloods }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date:today(), marker:"", value:"", unit:"", notes:"" });
  const [customMarker, setCustomMarker] = useState("");
  const [chartMarker, setChartMarker] = useState(null);
  const add = async () => {
  const marker = form.marker==="Custom" ? customMarker : form.marker;
  if (!marker || !form.date || !form.value) return;
  const entry = {
    ...form,
    marker,
    created_at: new Date().toISOString(),
  };
  delete entry.id;
  try {
    const added = await BloodResults.add(entry);
    setBloods([added, ...bloods]);
    setShowForm(false);
    setForm({ date:today(), marker:"", value:"", unit:"", notes:"" });
    setCustomMarker("");
  } catch (err) {
    console.error('Failed to add blood result:', err);
  }
};

const del = async (id) => {
  try {
    await BloodResults.remove(id);
    setBloods(bloods.filter(b => b.id!==id));
  } catch (err) {
    console.error('Failed to delete blood result:', err);
  }
};
  
  // All unique markers that have 2+ entries
  const markerNames = [...new Set(bloods.map(b => b.marker))];
  const trendableMarkers = markerNames.filter(m => bloods.filter(b => b.marker===m).length >= 1);

  // Chart data for selected marker
  const chartData = chartMarker
    ? bloods.filter(b => b.marker===chartMarker)
        .sort((a,b) => a.date.localeCompare(b.date))
        .map(b => ({ date: b.date, value: parseFloat(b.value), label: fmt(b.date) }))
    : [];

  const grouped = bloods.reduce((acc, b) => {
    if (!acc[b.date]) acc[b.date]=[];
    acc[b.date].push(b); return acc;
  }, {});

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontFamily:C.mono, fontSize:11, color:C.muted, letterSpacing:"0.1em" }}>BLOOD WORK</div>
          <div style={{ fontSize:20, fontWeight:700, color:C.text }}>{bloods.length} markers logged</div>
        </div>
        <Btn onClick={() => setShowForm(true)}>+ Add Result</Btn>
      </div>

      {/* Trend chart */}
      {trendableMarkers.length > 0 && (
        <Card>
          <div style={{ fontFamily:C.mono, fontSize:11, color:C.muted, letterSpacing:"0.1em", marginBottom:12 }}>TREND CHART</div>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:12 }}>
            {trendableMarkers.map((m, i) => (
              <button key={m} onClick={() => setChartMarker(chartMarker===m ? null : m)}
                style={{ background: chartMarker===m ? CHART_COLORS[i%CHART_COLORS.length]+"33" : "transparent",
                  border: `1px solid ${CHART_COLORS[i%CHART_COLORS.length]}${chartMarker===m?"":"44"}`,
                  color: CHART_COLORS[i%CHART_COLORS.length], borderRadius:4, padding:"4px 10px",
                  fontSize:11, fontFamily:C.mono, cursor:"pointer", letterSpacing:"0.05em" }}>
                {m}
              </button>
            ))}
          </div>
          {chartMarker && chartData.length > 0 && (
            <div style={{ height:180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top:4, right:8, bottom:4, left:0 }}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fill:C.muted, fontSize:9, fontFamily:C.mono }} />
                  <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:C.mono }} />
                  <Tooltip
                    contentStyle={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, fontFamily:C.mono, fontSize:11 }}
                    labelStyle={{ color:C.muted }}
                    itemStyle={{ color:C.accent }}
                  />
                  <Line type="monotone" dataKey="value" stroke={C.accent} strokeWidth={2}
                    dot={{ fill:C.accent, r:4 }} activeDot={{ r:6 }} name={chartMarker} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {!chartMarker && (
            <div style={{ textAlign:"center", padding:"20px 0", color:C.muted, fontSize:12 }}>Select a marker above to view its trend</div>
          )}
        </Card>
      )}

      {bloods.length===0 && <Card style={{ textAlign:"center", padding:40 }}><div style={{ color:C.muted, fontSize:14 }}>No blood work logged yet.</div></Card>}

      {Object.keys(grouped).sort((a,b) => b.localeCompare(a)).map(date => (
        <div key={date}>
          <div style={{ fontFamily:C.mono, fontSize:11, color:C.green, letterSpacing:"0.1em", marginBottom:8 }}>{fmt(date)}</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:8 }}>
            {grouped[date].map(b => (
              <Card key={b.id} style={{ position:"relative", padding:14 }}>
                <button onClick={() => del(b.id)} style={{ position:"absolute", top:6, right:8,
                  background:"none", border:"none", color:C.muted, cursor:"pointer", fontSize:14 }}>✕</button>
                <div style={{ fontSize:11, color:C.muted, fontFamily:C.mono, marginBottom:4 }}>{b.marker}</div>
                <div style={{ fontSize:22, fontWeight:700, color:C.text, fontFamily:C.mono }}>
                  {b.value}<span style={{ fontSize:12, color:C.muted, marginLeft:4 }}>{b.unit}</span>
                </div>
                {b.notes && <div style={{ fontSize:11, color:C.muted, marginTop:6 }}>{b.notes}</div>}
              </Card>
            ))}
          </div>
        </div>
      ))}

      {showForm && (
        <Modal title="ADD BLOOD RESULT" onClose={() => setShowForm(false)}>
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <Input label="Date" type="date" value={form.date} onChange={e => setForm({...form, date:e.target.value})} />
            <Select label="Marker" value={form.marker} onChange={e => setForm({...form, marker:e.target.value})}>
              <option value="">— Select marker —</option>
              {BLOOD_MARKERS.map(m => <option key={m}>{m}</option>)}
            </Select>
            {form.marker==="Custom" && (
              <Input label="Custom Marker Name" value={customMarker} onChange={e => setCustomMarker(e.target.value)} placeholder="e.g. GH pulse" />
            )}
            <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:10 }}>
              <Input label="Value" type="number" value={form.value} onChange={e => setForm({...form, value:e.target.value})} placeholder="25.4" />
              <Select label="Unit" value={form.unit} onChange={e => setForm({...form, unit:e.target.value})}>
  <option value="">— Select unit —</option>
  <option value="nmol/L">nmol/L</option>
  <option value="mg/dL">mg/dL</option>
  <option value="mIU/L">mIU/L</option>
  <option value="ng/mL">ng/mL</option>
  <option value="mcg/dL">mcg/dL</option>
  <option value="pg/mL">pg/mL</option>
  <option value="pmol/L">pmol/L</option>
  <option value="U/L">U/L</option>
  <option value="g/dL">g/dL</option>
  <option value="mmol/L">mmol/L</option>
  <option value="µIU/mL">µIU/mL</option>
  <option value="ng/dL">ng/dL</option>
  <option value="pg/dL">pg/dL</option>
  <option value="Other">Other</option>
</Select>
            </div>
            <Textarea label="Notes" value={form.notes} onChange={e => setForm({...form, notes:e.target.value})} placeholder="Lab, reference range, observations..." />
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <Btn variant="ghost" onClick={() => setShowForm(false)}>Cancel</Btn>
              <Btn onClick={add}>Save Result</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Tab: Notes ────────────────────────────────────────────────────────────────
function NotesTab({ notes, setNotes }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date:today(), title:"", body:"", tags:"" });

 const add = async () => {
  if (!form.title.trim() && !form.body.trim()) return;
  const entry = {
    ...form,
    created_at: new Date().toISOString(),
  };
  delete entry.id;
  try {
    const added = await Notes.add(entry);
    setNotes([added, ...notes]);
    setShowForm(false);
    setForm({ date:today(), title:"", body:"", tags:"" });
  } catch (err) {
    console.error('Failed to add note:', err);
  }
};

 const del = async (id) => {
  try {
    await Notes.remove(id);
    setNotes(notes.filter(n => n.id!==id));
  } catch (err) {
    console.error('Failed to delete note:', err);
  }
};

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontFamily:C.mono, fontSize:11, color:C.muted, letterSpacing:"0.1em" }}>NOTES & OBSERVATIONS</div>
          <div style={{ fontSize:20, fontWeight:700, color:C.text }}>{notes.length} notes</div>
        </div>
        <Btn onClick={() => setShowForm(true)}>+ Add Note</Btn>
      </div>

      {notes.length===0 && (
        <Card style={{ textAlign:"center", padding:40 }}>
          <div style={{ color:C.muted, fontSize:14 }}>No notes yet. Log side effects, progress, or anything relevant.</div>
        </Card>
      )}

      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {notes.map(n => (
          <Card key={n.id}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap" }}>
                  <span style={{ fontFamily:C.mono, fontSize:11, color:C.yellow }}>{fmt(n.date)}</span>
                  {n.tags && n.tags.split(",").map(t => <Badge key={t} color={C.muted}>{t.trim()}</Badge>)}
                </div>
                {n.title && <div style={{ fontWeight:600, fontSize:15, color:C.text, marginBottom:6 }}>{n.title}</div>}
                {n.body && <div style={{ fontSize:13, color:"#99B5CC", lineHeight:1.6, whiteSpace:"pre-wrap" }}>{n.body}</div>}
              </div>
              <Btn variant="danger" style={{ padding:"4px 10px", fontSize:12, marginLeft:8 }} onClick={() => del(n.id)}>✕</Btn>
            </div>
          </Card>
        ))}
      </div>

      {showForm && (
        <Modal title="ADD NOTE" onClose={() => setShowForm(false)}>
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <Input label="Date" type="date" value={form.date} onChange={e => setForm({...form, date:e.target.value})} />
            <Input label="Title (optional)" value={form.title} onChange={e => setForm({...form, title:e.target.value})} placeholder="e.g. Week 4 check-in" />
            <Textarea label="Note" value={form.body} onChange={e => setForm({...form, body:e.target.value})} placeholder="Side effects, energy, sleep, libido, recovery, mood..." style={{ minHeight:120 }} />
            <Input label="Tags (comma separated)" value={form.tags} onChange={e => setForm({...form, tags:e.target.value})} placeholder="side-effects, week-4" />
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <Btn variant="ghost" onClick={() => setShowForm(false)}>Cancel</Btn>
              <Btn onClick={add}>Save Note</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Tab: Cycles ────────────────────────────────────────────────────────────────
function CyclesTab({ cycles, setCycles, compounds, setCompounds }) {
  const [showCycleForm, setShowCycleForm] = useState(false);
  const [editingCycleId, setEditingCycleId] = useState(null);
  const [cycleForm, setCycleForm] = useState({ name:"", startDate:today(), endDate:"", notes:"" });
  const [cycleError, setCycleError] = useState("");
  const [cycleCompounds, setCycleCompounds] = useState([]);
  const [newDose, setNewDose] = useState({ libraryName: "", dose: "", unit: "mg", frequency: "Weekly", route: "" });
  const [library, setLibrary] = useState([]);

  useEffect(() => {
    CompoundLibrary.list().then(setLibrary).catch(err => console.error('Failed to load library:', err));
  }, []);

  const addCompoundToCycle = async () => {
    if (!newDose.libraryName || !newDose.dose) return;

    let compound = compounds.find(c => c.name === newDose.libraryName);

    if (!compound) {
      const lib = library.find(l => l.name === newDose.libraryName);
      const newCompound = {
        name: lib.name,
        category: lib.category,
        half_life: lib.half_life_hours || null,
        tmax_hours: lib.tmax_hours || 12,
        fast_ratio: lib.fast_ratio || 0.35,
        tracking_type: lib.tracking_type || 'pk_curve',
        confidence: lib.confidence || '',
        source_citation: lib.source_citation || '',
        source_url: lib.source_url || '',
        source_notes: lib.source_notes || '',
        route: lib.route || 'SubQ',
        unit: newDose.unit,
        dose: parseFloat(newDose.dose),
        frequency: newDose.frequency,
        active: true,
        notes: "",
        created_at: new Date().toISOString(),
      };
      compound = await Compounds.add(newCompound);
      setCompounds(prev => [compound, ...prev]);
    }

    setCycleCompounds([...cycleCompounds, { ...newDose, id: uid(), compoundId: compound.id, compoundName: compound.name }]);
    setNewDose({ libraryName: "", dose: "", unit: "mg", frequency: "Weekly", route: "" });
  };

  const removeCompoundFromCycle = (id) => {
    setCycleCompounds(cycleCompounds.filter(cc => cc.id !== id));
  };
const saveCycle = async () => {
  if (!cycleForm.name || !cycleForm.startDate) {
    setCycleError("Cycle name and start date are required.");
    return;
  }
  const entry = {
    name: cycleForm.name,
    start_date: cycleForm.startDate,
    end_date: cycleForm.endDate || null,
    notes: cycleForm.notes,
  };
  try {
    let cycleRecord;
    if (editingCycleId) {
      // Editing: update the cycle, then wipe and regenerate its doses/schedule (overwrite behavior)
      cycleRecord = await Cycles.update(editingCycleId, entry);
      await CycleDoses.removeAllForCycle(editingCycleId);
      await LogEntries.removeAllForCycle(editingCycleId);
      setCycles(cycles.map(c => c.id === editingCycleId ? cycleRecord : c));
       } else {
      cycleRecord = await Cycles.add({ ...entry, created_at: new Date().toISOString() });
      setCycles([cycleRecord, ...cycles]);
    }
    for (const cc of cycleCompounds) {

      await CycleDoses.add({
        cycle_id: cycleRecord.id,
        compound_id: cc.compoundId,
        compound_name: cc.compoundName,
        dose: parseFloat(cc.dose),
        unit: cc.unit,
        frequency: cc.frequency,
        start_date: cycleForm.startDate,
        end_date: cycleForm.endDate || null,
        route: cc.route || null,
      });

      const compound = compounds.find(c => c.id === cc.compoundId);
      const scheduleDates = expandScheduleDates(cc.frequency, cycleForm.startDate, cycleForm.endDate);
      const plannedRows = scheduleDates.map(date => ({
        compound_id: cc.compoundId,
        compound_name: cc.compoundName,
        dose: parseFloat(cc.dose),
        unit: cc.unit,
       date: new Date(`${date}T12:00:00`).toISOString(),
        status: 'planned',
        cycle_id: cycleRecord.id,
        tmax_hours: compound?.tmax_hours,
        half_life: compound?.half_life,
        fast_ratio: compound?.fast_ratio,
        fast_phase_hours: compound?.fast_phase_hours,
        created_at: new Date().toISOString(),
      }));
      if (plannedRows.length > 0) {
        await LogEntries.bulkAdd(plannedRows);
      }
    }

    setShowCycleForm(false);
    setEditingCycleId(null);
    setCycleForm({ name:"", startDate:today(), endDate:"", notes:"" });
    setCycleCompounds([]);
    setCycleError("");
  } catch (err) {
    const message = err?.message || (err?.error ? err.error : String(err));
    console.error('Failed to save cycle:', entry, err);
    setCycleError(message || "Unable to save cycle. Please try again.");
  }
};

const openEditCycle = async (cycle) => {
  setEditingCycleId(cycle.id);
  setCycleForm({
    name: cycle.name,
    startDate: cycle.start_date,
    endDate: cycle.end_date || "",
    notes: cycle.notes || "",
  });
  try {
    const existingDoses = await CycleDoses.listForCycle(cycle.id);
    setCycleCompounds(existingDoses.map(d => ({
      id: uid(),
      compoundId: d.compound_id,
      compoundName: d.compound_name,
      dose: d.dose,
      unit: d.unit,
      frequency: d.frequency,
      route: d.route || "",
    })));
  } catch (err) {
    console.error('Failed to load cycle doses for editing:', err);
    setCycleCompounds([]);
  }
  setShowCycleForm(true);
};

  const delCycle = async (id) => {
    try {
      await CycleDoses.removeAllForCycle(id);
      await LogEntries.removeAllForCycle(id);
      await Cycles.remove(id);
      setCycles(cycles.filter(c => c.id!==id));
    } catch (err) {
      console.error('Failed to delete cycle:', err);
    }
  };

  const endCycle = async (id) => {
    try {
      await Cycles.update(id, { end_date: today() });
      setCycles(cycles.map(c => c.id===id ? {...c, end_date:today()} : c));
    } catch (err) {
      console.error('Failed to end cycle:', err);
    }
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontFamily:C.mono, fontSize:11, color:C.muted, letterSpacing:"0.1em" }}>CYCLES | PROTOCOLS</div>
          <div style={{ fontSize:20, fontWeight:700, color:C.text }}>{cycles.length} cycles | protocols</div>
        </div>
        <Btn onClick={() => { setEditingCycleId(null); setCycleForm({ name:"", startDate:today(), endDate:"", notes:"" }); setCycleCompounds([]); setShowCycleForm(true); }}>+ New Cycle | Protocol</Btn>
      </div>

      {showCycleForm && (
        <Modal title={editingCycleId ? "EDIT CYCLE | PROTOCOL" : "NEW CYCLE | PROTOCOL"} onClose={() => { setShowCycleForm(false); setEditingCycleId(null); setCycleCompounds([]); }}>
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <Input label="Cycle Name" value={cycleForm.name} onChange={(e) => setCycleForm({...cycleForm, name:e.target.value})} placeholder="e.g. BCP 157/TB500" />
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <Input label="Start Date" type="date" value={cycleForm.startDate} onChange={(e) => setCycleForm({...cycleForm, startDate:e.target.value})} />
              <Input label="End Date" type="date" value={cycleForm.endDate} onChange={(e) => setCycleForm({...cycleForm, endDate:e.target.value})} />
            </div>
            <Textarea label="Notes" value={cycleForm.notes} onChange={(e) => setCycleForm({...cycleForm, notes:e.target.value})} placeholder="Goals, observations..." />

            <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:14 }}>
              <div style={{ fontFamily:C.mono, fontSize:11, color:C.muted, letterSpacing:"0.08em", marginBottom:10 }}>COMPOUND STACK</div>

              {cycleCompounds.length > 0 && (
                <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:12 }}>
                  {cycleCompounds.map(cc => (
                    <div key={cc.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 10px", background:C.bg, borderRadius:6, border:`1px solid ${C.border}` }}>
                      <div style={{ fontSize:13, color:C.text }}>
                        {cc.compoundName} — {cc.dose}{cc.unit} {cc.frequency}
                      </div>
                      <button onClick={() => removeCompoundFromCycle(cc.id)} style={{ background:"none", border:"none", color:C.muted, cursor:"pointer", fontSize:14 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                <Select label="Compound" value={newDose.libraryName} onChange={e => setNewDose({...newDose, libraryName:e.target.value})}>
                  <option value="">— Select compound —</option>
                  {Object.entries(
                    library.reduce((groups, l) => {
                      const cat = l.category || 'Other';
                      if (!groups[cat]) groups[cat] = [];
                      groups[cat].push(l);
                      return groups;
                    }, {})
                  )
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([category, compoundsInGroup]) => (
                      <optgroup key={category} label={category}>
                        {compoundsInGroup
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(l => <option key={l.name} value={l.name}>{l.name}</option>)}
                      </optgroup>
                    ))}
                </Select>
                <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:10 }}>
                  <Input label="Dose" type="number" value={newDose.dose} onChange={e => setNewDose({...newDose, dose:e.target.value})} placeholder="500" />
                  <Select label="Unit" value={newDose.unit} onChange={e => setNewDose({...newDose, unit:e.target.value})}>
                    {["mg","mcg","IU","ml"].map(u => <option key={u}>{u}</option>)}
                  </Select>
                </div>
                <Select label="Frequency" value={newDose.frequency} onChange={e => setNewDose({...newDose, frequency:e.target.value})}>
                  {["Daily","EOD","2x/week","Weekly","Monthly"].map(f => <option key={f}>{f}</option>)}
                </Select>
                <Btn variant="ghost" onClick={addCompoundToCycle}>+ Add to Stack</Btn>
              </div>
            </div>

            {cycleError && <div style={{ color:C.red, fontSize:13, marginTop:4 }}>{cycleError}</div>}
<div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
  <Btn variant="ghost" onClick={() => { setShowCycleForm(false); setEditingCycleId(null); setCycleError(""); setCycleCompounds([]); }}>Cancel</Btn>
  <Btn onClick={saveCycle}>{editingCycleId ? "Update Cycle" : "Save Cycle"}</Btn>
</div>
          </div>
        </Modal>
      )}

      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
  {cycles.map(c => (
    <CycleCard key={c.id} cycle={c} onEnd={endCycle} onDelete={delCycle} onEdit={openEditCycle} compounds={compounds} />
  ))}
</div>

      {cycles.length===0 && (
        <Card style={{ textAlign:"center", padding:40 }}>
          <div style={{ fontFamily:C.mono, fontSize:11, color:C.muted, letterSpacing:"0.08em" }}>NO CYCLES YET</div>
          <div style={{ fontSize:13, color:C.muted, marginTop:8 }}>Start a new cycle to track your protocols.</div>
        </Card>
      )}
    </div>
  );
}

function CycleCard({ cycle, onEnd, onDelete, onEdit, compounds }) {
  const [doses, setDoses] = useState([]);

  useEffect(() => {
    CycleDoses.listForCycle(cycle.id).then(setDoses).catch(err => console.error('Failed to load cycle doses:', err));
  }, [cycle.id]);

  const pkPreviews = doses
    .map(d => {
      const compound = compounds.find(c => c.id === d.compound_id);
      if (!compound || compound.tracking_type === 'simple_log') return null;

      const scheduleDates = expandScheduleDates(d.frequency, d.start_date, d.end_date);
      const plannedDoses = scheduleDates.map(date => ({
        date: date + "T12:00:00",
        dose: d.dose,
        tmax_hours: compound.tmax_hours,
        half_life: compound.half_life,
        fast_ratio: compound.fast_ratio,
        fast_phase_hours: compound.fast_phase_hours,
      }));

      const halfLife = compound.half_life || 120;
      const previewEnd = d.end_date || new Date(new Date(d.start_date).getTime() + 90 * 86400000).toISOString().slice(0, 10);
      const points = calcPKPointsForDateRange(plannedDoses, halfLife, d.start_date, previewEnd);
      if (points.length === 0) return null;

      const peak = Math.max(...points.map(p => p.level));

      return { compoundName: d.compound_name, points, peak };
    })
    .filter(Boolean);

  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, padding:16 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"start" }}>
        <div>
          <div style={{ fontFamily:C.mono, fontSize:13, fontWeight:700, color:C.accent }}>{cycle.name}</div>
          <div style={{ fontSize:12, color:C.muted, marginTop:4 }}>{fmt(cycle.start_date)} {cycle.end_date ? `→ ${fmt(cycle.end_date)}` : "→ Ongoing"}</div>
          {cycle.notes && <div style={{ fontSize:13, color:C.text, marginTop:8 }}>{cycle.notes}</div>}
        </div>
       <div style={{ display:"flex", gap:6 }}>
  <Btn variant="ghost" onClick={() => onEdit(cycle)}>Edit</Btn>
  {!cycle.end_date && <Btn variant="ghost" onClick={() => onEnd(cycle.id)}>End</Btn>}
  <Btn variant="ghost" onClick={() => onDelete(cycle.id)}>Delete</Btn>
</div>
      </div>

      {doses.length > 0 && (
        <div style={{ marginTop:12, paddingTop:12, borderTop:`1px solid ${C.border}` }}>
          <div style={{ fontFamily:C.mono, fontSize:10, color:C.muted, letterSpacing:"0.08em", marginBottom:8 }}>STACK</div>
          <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
            {doses.map(d => (
              <div key={d.id} style={{ fontSize:13, color:C.text }}>
                {d.compound_name || "Compound"} — {d.dose}{d.unit} {d.frequency}
              </div>
            ))}
          </div>
        </div>
      )}

      {pkPreviews.length > 0 && (
        <div style={{ marginTop:16, paddingTop:12, borderTop:`1px solid ${C.border}` }}>
          <div style={{ fontFamily:C.mono, fontSize:10, color:C.muted, letterSpacing:"0.08em", marginBottom:12 }}>PROJECTED LEVELS (PLANNED)</div>
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            {pkPreviews.map((preview, i) => {
              const step = Math.max(1, Math.floor(preview.points.length / 60));
              const chartData = preview.points.filter((_, idx) => idx % step === 0);
              const color = GRAPH_COLORS[i % GRAPH_COLORS.length];

              return (
                <div key={preview.compoundName}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:6 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{preview.compoundName}</div>
                    <div style={{ fontSize:11, color:C.muted, fontFamily:C.mono }}>peak ~{preview.peak.toFixed(2)}</div>
                  </div>
                  <ResponsiveContainer width="100%" height={100}>
                    <LineChart data={chartData} margin={{ top:4, right:4, bottom:4, left:4 }}>
                      <Line type="monotone" dataKey="level" stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
                      <XAxis dataKey="label" hide />
                      <YAxis hide />
                      <Tooltip
                        contentStyle={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, fontFamily:C.mono, fontSize:11 }}
                        labelStyle={{ color:C.muted }}
                        formatter={(v) => [parseFloat(v).toFixed(2), "Level"]}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab: Export ───────────────────────────────────────────────────────────────
function ExportTab({ compounds, logs, bloods, notes, cycles }) {
  const exportJSON = () => {
    const data = { exportDate: new Date().toISOString(), compounds, logs, bloods, notes };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type:"application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `protocol-export-${today()}.json`;
    a.click();
  };

  const exportCSV = () => {
    const rows = [["Date","Compound","Dose","Unit","Site","Notes"]];
    logs.forEach(l => rows.push([l.date, l.compoundName, l.dose, l.unit, l.site||"", l.notes||""]));
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type:"text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `injection-log-${today()}.csv`;
    a.click();
  };

  const exportBloodCSV = () => {
    const rows = [["Date","Marker","Value","Unit","Notes"]];
    bloods.forEach(b => rows.push([b.date, b.marker, b.value, b.unit||"", b.notes||""]));
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type:"text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `blood-work-${today()}.csv`;
    a.click();
  };

  const printReport = () => {
    const activeCycle = cyclesState.find(c => !c.endDate || c.endDate >= today());
    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Protocol Report — ${today()}</title>
<style>
  body { font-family: -apple-system, sans-serif; color: #111; max-width: 800px; margin: 40px auto; padding: 0 20px; }
  h1 { font-size: 22px; border-bottom: 2px solid #000; padding-bottom: 8px; }
  h2 { font-size: 15px; color: #444; margin-top: 28px; text-transform: uppercase; letter-spacing: 0.08em; }
  table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px; }
  th { text-align: left; padding: 6px 8px; background: #f0f0f0; border: 1px solid #ccc; font-size: 11px; text-transform: uppercase; }
  td { padding: 6px 8px; border: 1px solid #ddd; vertical-align: top; }
  .meta { color: #666; font-size: 12px; margin-bottom: 20px; }
  .cycle-banner { background: #f5f5f5; border-left: 4px solid #333; padding: 12px 16px; margin: 16px 0; border-radius: 4px; }
  @media print { body { margin: 20px; } }
</style>
</head>
<body>
<h1>Protocol Report</h1>
<p class="meta">Generated: ${new Date().toLocaleString("en-NZ")} &nbsp;|&nbsp; ${compounds.filter(c=>c.active).length} active compounds &nbsp;|&nbsp; ${logs.length} log entries</p>

${activeCycle ? `<div class="cycle-banner"><strong>${activeCycle.name}</strong> — ${fmt(activeCycle.startDate)}${activeCycle.endDate ? ` → ${fmt(activeCycle.endDate)}` : " (ongoing)"} &nbsp; <strong>${cycleDayLabel(activeCycle.startDate, today())}</strong>${activeCycle.notes ? `<br/><em>${activeCycle.notes}</em>` : ""}</div>` : ""}

<h2>Active Compounds</h2>
<ta
<tr><th>Name</th><th>Category</th><th>Dose</th><th>Frequency</th><th>Route</th><th>Notes</th></tr>
${compounds.filter(c=>c.active).map(c=>`<tr><td>${c.name}</td><td>${c.category}</td><td>${c.dose} ${c.unit}</td><td>${c.frequency}</td><td>${c.route}</td><td>${c.notes||""}</td></tr>`).join("")}
</table>


<h2>Administration Log (last 30 entries)</h2>
<table>
<tr><th>Date</th><th>Compound</th><th>Dose</th><th>Site</th><th>Notes</th></tr>
${logs.slice(0,30).map(l=>`<tr><td>${fmt(l.date)}</td><td>${l.compoundName}</td><td>${l.dose} ${l.unit}</td><td>${l.site||""}</td><td>${l.notes||""}</td></tr>`).join("")}
</table>

<h2>Blood Work (most recent)</h2>
<table>
<tr><th>Date</th><th>Marker</th><th>Value</th><th>Unit</th><th>Notes</th></tr>
${bloods.slice(0,30).map(b=>`<tr><td>${fmt(b.date)}</td><td>${b.marker}</td><td>${b.value}</td><td>${b.unit||""}</td><td>${b.notes||""}</td></tr>`).join("")}
</table>

${notes.length > 0 ? `<h2>Notes</h2>
<table>
<tr><th>Date</th><th>Title</th><th>Note</th><th>Tags</th></tr>
${notes.slice(0,20).map(n=>`<tr><td>${fmt(n.date)}</td><td>${n.title||""}</td><td>${n.body||""}</td><td>${n.tags||""}</td></tr>`).join("")}
</table>` : ""}
</body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
    w.print();
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>


      {/* Export options */}
      <Card>
        <div style={{ fontFamily:C.mono, fontSize:11, color:C.muted, letterSpacing:"0.1em", marginBottom:16 }}>EXPORT DATA</div>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          <ExportRow
            label="Print / Save as PDF"
            desc="Full protocol report — compounds, log, blood work, notes"
            action="Print Report"
            onClick={printReport}
            color={C.accent}
          />
          <ExportRow
            label="Injection Log CSV"
            desc="All logged doses — import into Excel or Sheets"
            action="Download CSV"
            onClick={exportCSV}
            color={C.green}
          />
          <ExportRow
            label="Blood Work CSV"
            desc="All blood markers with dates and values"
            action="Download CSV"
            onClick={exportBloodCSV}
            color={C.green}
          />
          <ExportRow
            label="Full Backup (JSON)"
            desc="Everything — compounds, logs, bloods, notes, cycles"
            action="Download JSON"
            onClick={exportJSON}
            color={C.yellow}
          />
        </div>
      </Card>
    </div>
  );
}

function ExportRow({ label, desc, action, onClick, color }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
      padding:"12px 14px", background:C.bg, borderRadius:8, border:`1px solid ${C.border}` }}>
      <div>
        <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{label}</div>
        <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{desc}</div>
      </div>
      <button onClick={onClick} style={{ background:color+"22", border:`1px solid ${color}44`,
        color, borderRadius:6, padding:"6px 14px", fontSize:12, fontFamily:C.sans,
        fontWeight:600, cursor:"pointer", whiteSpace:"nowrap", marginLeft:12 }}>
        {action}
      </button>
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
function AppContent({ user, signOut }) {
  const [subscription, setSubscription] = useState(null);
  const [tab, setTab] = useState(0);
  const [ready, setReady] = useState(false);
  const [compounds, setCompounds] = useState([]);
  const [logs, setLogs] = useState([]);
  const [bloods, setBloods] = useState([]);
  const [notes, setNotes] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [body, setBody] = useState({ entries: [], goal: {} });
  const [batches, setBatches] = useState([]);

  useEffect(() => {
  (async () => {
    try {
     const [c, l, b, n, cy, bat, entries] = await Promise.all([
  Compounds.list(),
  LogEntries.list(),
  BloodResults.list(),
  Notes.list(),
  Cycles.list(),
  Batches.list(),
  BodyEntries.list(),

      ]);
      if (c) setCompounds(c);
      if (l) setLogs(l);
      if (b) setBloods(b);
      if (n) setNotes(n);
      if (cy) setCycles(cy);
      if (bat) setBatches(bat);
      
      const goal = await Goal.get();
      setBody({ entries: entries || [], goal: goal || {} });
      
      setReady(true);
    } catch (err) {
      console.error('Failed to load data:', err);
      setReady(true);
    }
  })();
}, []);

useEffect(() => {
  (async () => {
    try {
      console.log('Fetching subscription for user:', user.id);
      const { data, error } = await supabase.from('subscriptions').select('*').eq('user_id', user.id).maybeSingle();
      console.log('Subscription data:', data);
      console.log('Subscription error:', error);
      setSubscription(data);
    } catch (err) {
      console.error('Subscription check failed:', err);
    }
  })();
}, [user.id]);
  if (!ready) return (
    <div style={{ background:C.bg, minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <span style={{ fontFamily:C.mono, color:C.muted, fontSize:13, letterSpacing:"0.1em" }}>LOADING...</span>
    </div>
  );
  
const handleUpgrade = async (priceId) => {
  try {
    const response = await fetch('/.netlify/functions/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: user.id,
        email: user.email,
        priceId: priceId,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
      return;
    }

    const { sessionId } = await response.json();
    if (window.Stripe) {
      await window.Stripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY).redirectToCheckout({ sessionId });
    }
  } catch (err) {
    console.error('Upgrade failed:', err);
    alert('Checkout failed. Please try again.');
  }
};
// Trial expired / no active subscription — block access
const trialExpired = subscription && subscription.trial_end_date && new Date(subscription.trial_end_date) < new Date() && subscription.status !== 'active';
const noSubscription = !subscription;

if (trialExpired || noSubscription) {
  return (
    <div style={{ background:C.bg, minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", fontFamily:C.sans, color:C.text, padding:20, textAlign:"center" }}>
      <div style={{ fontFamily:C.mono, fontSize:14, letterSpacing:"0.12em", color:C.text, fontWeight:700, marginBottom:8 }}>PROTOCOL tracker</div>
      <div style={{ fontSize:20, fontWeight:700, marginBottom:8 }}>
        {trialExpired ? "Your trial has ended" : "Subscribe to continue"}
      </div>
      <div style={{ fontSize:13, color:C.muted, marginBottom:24, maxWidth:320 }}>
        Upgrade to keep tracking your protocols, blood work, and PK graphs.
      </div>
      <div style={{ display:"flex", gap:8 }}>
        <Btn onClick={() => handleUpgrade(process.env.REACT_APP_STRIPE_PRICE_MONTHLY)}>Upgrade (Monthly)</Btn>
        <Btn onClick={() => handleUpgrade(process.env.REACT_APP_STRIPE_PRICE_ANNUAL)}>Upgrade (Annual)</Btn>
      </div>
      <Btn variant="ghost" onClick={signOut} style={{ marginTop:20, fontSize:12 }}>Sign Out</Btn>
    </div>
  );
}
  const activeCycle = cycles.find(c => !c.endDate || c.endDate >= today());

return (
  <div style={{ 
  background: C.bg, 
  minHeight: "100vh", 
  fontFamily: C.sans, 
  color: C.text,
  fontSize: window.innerWidth < 768 ? 14 : 16,
}}>
  <div style={{ borderBottom:`1px solid ${C.border}`, padding:"16px 20px", display:"flex", alignItems:"center", gap:12 }}>
      <div style={{ width:8, height:8, borderRadius:"50%", background:C.accent, boxShadow:`0 0 8px ${C.accent}` }} />
      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
        <span style={{ fontFamily:C.mono, fontSize:14, letterSpacing:"0.12em", color:C.text, fontWeight:700 }}>PROTOCOL</span>
        <span style={{ fontFamily:C.mono, fontSize:11, color:C.muted, letterSpacing:"0.08em" }}>tracker</span>
      </div>
      <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:12 }}>
        {activeCycle && (
          <span style={{ fontFamily:C.mono, fontSize:11, color:C.accent }}>
            {activeCycle.name} · {cycleDayLabel(activeCycle.startDate, today())}
          </span>
        )}
        {subscription && subscription.trial_end_date && (
          <span style={{ fontFamily:C.mono, fontSize:11, color:C.yellow, marginLeft:12 }}>
            Trial: {Math.max(0, Math.ceil((new Date(subscription.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24)))} days left
          </span>
        )}
        {subscription && !subscription.trial_end_date && subscription.status === 'active' && (
          <span style={{ fontFamily:C.mono, fontSize:11, color:C.green, marginLeft:12 }}>
            ✓ Pro · Active
          </span>
        )}
        <div style={{ display:"flex", gap:6 }}>
          {!subscription && (
            <>
              <Btn onClick={() => handleUpgrade(process.env.REACT_APP_STRIPE_PRICE_MONTHLY)} style={{ padding:"6px 12px", fontSize:12 }}>Upgrade (Monthly)</Btn>
              <Btn onClick={() => handleUpgrade(process.env.REACT_APP_STRIPE_PRICE_ANNUAL)} style={{ padding:"6px 12px", fontSize:12 }}>Upgrade (Annual)</Btn>
            </>
          )}
        </div>
        <Btn variant="ghost" onClick={signOut} style={{ padding:"6px 12px", fontSize:12 }}>Sign Out</Btn>
      </div>
    </div>
      {/* Tabs */}
      <div style={{ display:"flex", borderBottom:`1px solid ${C.border}`, overflowX:"auto" }}>
        {TABS.map((t,i) => (
          <button key={t} onClick={() => setTab(i)} style={{
            background:"none", border:"none", cursor:"pointer",
            padding:"12px 16px", fontFamily:C.mono, fontSize:10,
            letterSpacing:"0.1em", textTransform:"uppercase", whiteSpace:"nowrap",
            color: tab===i ? C.accent : C.muted,
            borderBottom: tab===i ? `2px solid ${C.accent}` : "2px solid transparent",
            transition:"color 0.15s",
          }}>{t}</button>
        ))}
      </div>

      {/* Content */}
      {/* Content */}
{/* Content */}
<div style={{ padding:20, maxWidth: tab===2 ? 1100 : 720, margin:"0 auto" }}>
  {tab===0 && <DashboardTab compounds={compounds} logs={logs} body={body} setBody={setBody} />}
  {tab===1 && <CompoundsTab compounds={compounds} setCompounds={setCompounds} />}
  {tab===2 && <MonthCalendar />}
 {tab===3 && <CyclesTab cycles={cycles} setCycles={setCycles} compounds={compounds} setCompounds={setCompounds} />}
  {tab===4 && <LogTab compounds={compounds} logs={logs} setLogs={setLogs} cycles={cycles} />}
  {tab===5 && <BloodTab bloods={bloods} setBloods={setBloods} />}
  {tab===6 && <NotesTab notes={notes} setNotes={setNotes} />}
  {tab===7 && <BatchesTab compounds={compounds} batches={batches} setBatches={setBatches} />}
  {tab===8 && <ReconstitutionTab />}
{tab===9 && <InjectionSitesTab />}
{tab===10 && <ExportTab compounds={compounds} logs={logs} bloods={bloods} notes={notes} cycles={cycles} />}
</div>
    </div>
  );
}
// ── Auth gate ─────────────────────────────────────────────────────────────────
function AuthGate() {
  const { user, loading, signOut, isRecovery, clearRecovery } = useAuth();

  if (loading) {
    return (
      <div style={{ background:"#070B10", minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
        <span style={{ fontFamily:"'JetBrains Mono','Fira Mono',monospace", color:"#5A7A99", fontSize:13, letterSpacing:"0.1em" }}>LOADING...</span>
      </div>
    );
  }

  if (isRecovery) return <AuthScreen isRecovery onRecoveryDone={clearRecovery} />;

  if (!user) return <AuthScreen />;

  return <AppContent user={user} signOut={signOut} />;
}

export default function App() {
  return (
    <Elements stripe={stripePromise}>
      <AuthProvider>
        <AuthGate />
      </AuthProvider>
    </Elements>
  );
}